import * as React from 'react';

import { useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import EwpFab from '../components/EwpFab';
import Header from '../components/Header';

import { useConfirmationDialog } from '../context/ConfirmationDialogContext';
import { useMessage } from '../context/MessageProvider';
import { useConnectivity } from '../context/ConnectivityProvider';

import { listLocations, deleteLocation } from '../models/locations';

export async function loader() {
  const locations = await listLocations();
  return { locations };
}

export default function LocationsListPage({ title = 'Locations', ...props }) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { locations } = useLoaderData();
  const { confirm } = useConfirmationDialog();
  const { setSuccess, setError } = useMessage();
  const { isOnline } = useConnectivity();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedLocation, setSelectedLocation] = React.useState(null);

  const sortedLocations = React.useMemo(() => {
    return [...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [locations]);

  const handleOpenMenu = (event, location) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedLocation(location);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedLocation(null);
  };

  const handleDelete = () => {
    const current = selectedLocation;
    handleCloseMenu();

    if (!current) return;

    confirm({
      title: 'Delete Location',
      message: (
        <div>
          <p style={{ margin: 0 }}>
            You are about to delete <strong>{current.name}</strong>.
            <br />
            Are you sure?
          </p>
          <p style={{ color: 'red', margin: 0 }}>
            This action cannot be undone
          </p>
        </div>
      ),
      onSubmit: async () => {
        try {
          await deleteLocation(current.id, isOnline);
          setSuccess('Location deleted');
          revalidator.revalidate();
        } catch (error) {
          console.error(error);
          setError(error);
        }
      },
    });
  };

  return (
    <Container component="main" maxWidth="md">
      <Header label={title} />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {sortedLocations.map((location) => (
          <Grid key={location.id} xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ position: 'relative' }}>
              <IconButton
                aria-label="location actions"
                onClick={(event) => handleOpenMenu(event, location)}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              >
                <MoreVertIcon />
              </IconButton>
              <CardActionArea onClick={() => navigate(`/locations/${location.id}`)}>
                <CardContent>
                  <Typography variant="h6">{location.name}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <EwpFab onClick={() => navigate('/locations/new')}>
        <AddIcon />
      </EwpFab>
    </Container>
  );
}
